import { useSession } from '@/auth/useSession.js';
import { Tooltip } from '@/components/tooltip.js';
import { Checkbox } from '@/components/ui/checkbox.js';
import { getAdminSessions, updateSessionDetails } from '@/lib/api-calls.js';
import { AdminSession } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';

type CheckboxUpdateSupportAdmin = AdminSession & {
  checkboxTestId?: string;
};

export const CheckboxUpdateSupportAdmin = ({ checkboxTestId, ...props }: CheckboxUpdateSupportAdmin) => {
  const { id, supportAdmin, status } = props;

  const queryClient = useQueryClient();
  const { actingUser } = useSession();
  const { mutateAsync: updateSessionDetailsMutate } = useMutation({ mutationFn: updateSessionDetails });
  const hasSupportAdmin = !!supportAdmin;

  const isActingUserCurrentSupportAdmin = hasSupportAdmin && supportAdmin.id === actingUser.id;

  const onCheckedChange = async () => {
    await updateSessionDetailsMutate({
      id,
      data: {
        status,
        // null disconnects; string assigns the acting user as support admin.
        supportAdminId: isActingUserCurrentSupportAdmin ? null : actingUser.id,
        shouldSendEmail: false,
      },
    });
    queryClient.invalidateQueries(getAdminSessions());
  };

  return (
    <Tooltip
      trigger={
        <Checkbox
          checked={hasSupportAdmin}
          onCheckedChange={onCheckedChange}
          aria-label="Assign Support Admin"
          data-testid={checkboxTestId}
        />
      }
    >
      {hasSupportAdmin ? (
        <span>
          Support admin is {isActingUserCurrentSupportAdmin ? 'the acting user' : supportAdmin.fullNameOrEmail}
        </span>
      ) : (
        <button onClick={onCheckedChange} aria-label="Assign support admin to yourself">
          Assign to me
        </button>
      )}
    </Tooltip>
  );
};
